// Pallette:
// primary - used to represent primary interface elements for a user. It's the color displayed most frequently across your app's screens and components.
// secondary - used to represent secondary interface elements for a user. It provides more ways to accent and distinguish your product. Having it is optional.
// error - used to represent interface elements that the user should be made aware of.
// warning - used to represent potentially dangerous actions or important messages.
// info - used to present information to the user that is neutral and not necessarily important.
// success - used to indicate the successful completion of an action that user triggered.

import spacing from './utils/spacing';

export const theme = {
  palette: {
    primary: { main: '#036D83', light: '#208da5' },
    secondary: '#E8AD44',
    error: '#C94553',
    errorContainer: '#F2D7DA',
    warning: '#F57C00',
    info: '#1976D2',
    success: '#388E3C',
    colors: {
      atoll: '#036D83',
      hover: '#DFEBEE',
      navy: '#555B70',
      midnight: '#3B3D64',
      dolphin: '#53B8B4',
      groupPill: '#68B5901A',
      groupPillBorder: '#68B59033',
      keppel: '#68B590',
      gulfStream: '#7AB4AB',
      surf: '#B2CFBB',
      moon: '#F5EACE',
      flax: '#EFCC89',
      highlight: '#8696A7',
      mojo: '#C33F3B',
      shiraz: '#B00836',
      access: '#E5173F',
      denim: '#0f7db5',
      coral: '#EB6F7B',
      plum: '#84497D',
      porcelain: '#eef0f2',
      finn: '#712E58',
      border: '#E2ECEF',
      rowHover: '#EDF5F6',
      disabledHover: '#CFDDE2',
      textBody: '#4B4E52',
      textPlaceholder: '#757578',
      fieldBorder: '#DBE0E5',
      buttonBorder: '#DBDFE4',
      background: '#F7F8FA',
      white: '#FFFFFF',
      defaultText: '#000',
      enabledInput: '#FFF',
      disabledInput: '#eef0f2',
      gridBorder: '#00000014',
      gridCellBorder: '#DEE0EA',
      gridSelectedRangeBorder: '#2196F3',
      placeholderTitle: '#6d7278',
      placeholderColor: '#6d7278',
      rightPanelBackground: '#F9FAFB',
      rightPanelBackgroundBottom: '#E8EAF1',
      rightPanelShadow: '#00000029',
      quickLinkLeftBar: '#B17916',
      deleteActionBackground: '#F0CCCF',
      textBodySecondary: '#4E5054',
      focusControl: '#8BB4BF',
      datePickerFocusState: '#D5763F',
      datePickerDivider: '#EEF0F1',
      datePickerText: '#7D7D80',
      agGridInput: '#181D1F',
      boxShadow: '#0000001a',
      inputBorder: '#8696A7',
      cardHover: '#F2F9FA',
      footerSeparator: '#5E5E5E',
      labelText: '#4C4F53',
      iconBadge: '#F3B43A',
      deleteIcon: '#575757',
      sunflower: '#E8AD44',
      overviewPanel: '#f4f5f8',
      rowPillInfoBackground: '#E5F1FA',
      rowPillInfoBorder: '#2658B8',
      rowPillErrorBackground: '#FBEAE7',
      rowPillErrorBorder: '#D82C0D',
      rowPillSuccessBackground: '#0c7510',
      rowPillSuccessBorder: '#0e9a1f',
      remittanceBacsProcessBorder: '#b1b8bf',
      remittanceBacsProcessHeaderLine: '#007a7a',
      remmitanceBacsProcess: '#202227',
      remittanceBacsProcessInput: '#4e5054',
      lightBackground: '#f9f9fa',
      actionGroupDelimiter: '#C4C4C4',
      scrollbar: '#A9A9A9',
      scrollbarHover: '#808080',
      label: '#202227',
      lightUiBackground: '#E5F2F2',
      surfaceBorder: '#828D98',
    },
  },
  typography: {
    fontFamily:
      'Roboto, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightMediumBold: 600,
    fontWeightBold: 700,
    fontWeightUltraBold: 800,

    h1: {
      fontSize: '48px',
      fontWeight: 400,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 400,
    },
    h3: {
      fontSize: '18px',
      fontWeight: 400,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 400,
    },
    h5: {
      fontSize: '17px',
      fontWeight: 700,
    },
    h6: {
      fontSize: '15px',
      fontWeight: 400,
    },
    feature: {
      fontSize: '16px',
      fontWeight: 700,
    },
    defaultBold: {
      fontSize: '13px',
      fontFamily: 'Roboto',
      fontWeight: 700,
    },
    defaultMedium: {
      fontSize: '13px',
      fontWeight: 500,
    },
    default: {
      fontSize: '13px',
      fontWeight: 400,
    },
    small: {
      fontSize: '12px',
      fontWeight: 400,
    },
    minimum: {
      fontSize: '11px',
      fontWeight: 400,
    },
    tiny: {
      fontSize: '9px',
      fontWeight: 400,
    },
    link: {
      fontSize: '14px',
      fontWeight: 500,
    },
    agGridInput: {
      fontSize: '14px',
      fontFamily: 'MS Shell Dlg 2',
      fontWeight: 400,
    },
  },
  spacing,
};

declare module 'styled-components' {
  export interface DefaultTheme {
    palette: {
      primary: { main: string; light: string };
      secondary: string;
      error: string;
      errorContainer: string;
      warning: string;
      info: string;
      success: string;
      colors: {
        atoll: string;
        hover: string;
        navy: string;
        midnight: string;
        dolphin: string;
        groupPill: string;
        groupPillBorder: string;
        keppel: string;
        gulfStream: string;
        surf: string;
        moon: string;
        flax: string;
        highlight: string;
        mojo: string;
        shiraz: string;
        access: string;
        denim: string;
        coral: string;
        plum: string;
        porcelain: string;
        finn: string;
        border: string;
        rowHover: string;
        disabledHover: string;
        textBody: string;
        textPlaceholder: string;
        fieldBorder: string;
        buttonBorder: string;
        background: string;
        white: string;
        defaultText: string;
        enabledInput: string;
        disabledInput: string;
        gridBorder: string;
        gridCellBorder: string;
        gridSelectedRangeBorder: string;
        placeholderTitle: string;
        placeholderColor: string;
        rightPanelBackground: string;
        rightPanelBackgroundBottom: string;
        rightPanelShadow: string;
        quickLinkLeftBar: string;
        deleteActionBackground: string;
        textBodySecondary: string;
        focusControl: string;
        datePickerFocusState: string;
        datePickerDivider: string;
        datePickerText: string;
        agGridInput: string;
        boxShadow: string;
        inputBorder: string;
        cardHover: string;
        footerSeparator: string;
        labelText: string;
        iconBadge: string;
        deleteIcon: string;
        sunflower: string;
        overviewPanel: string;
        rowPillInfoBackground: string;
        rowPillInfoBorder: string;
        rowPillErrorBackground: string;
        rowPillErrorBorder: string;
        rowPillSuccessBackground: string;
        rowPillSuccessBorder: string;
        remittanceBacsProcessBorder: string;
        remittanceBacsProcessHeaderLine: string;
        remmitanceBacsProcess: string;
        remittanceBacsProcessInput: string;
        lightBackground: string;
        actionGroupDelimiter: string;
        scrollbar: string;
        scrollbarHover: string;
        label: string;
        lightUiBackground: string;
        surfaceBorder: string;
      };
    };
    typography: {
      fontFamily: string;
      fontWeightLight: number;
      fontWeightRegular: number;
      fontWeightMedium: number;
      fontWeightMediumBold: number;
      fontWeightBold: number;
      fontWeightUltraBold: number;
      h1: { fontSize: string; fontWeight: number };
      h2: { fontSize: string; fontWeight: number };
      h3: { fontSize: string; fontWeight: number };
      h4: { fontSize: string; fontWeight: number };
      h5: { fontSize: string; fontWeight: number };
      h6: { fontSize: string; fontWeight: number };
      feature: { fontSize: string; fontWeight: number };
      defaultBold: { fontSize: string; fontFamily: string; fontWeight: number };
      defaultMedium: { fontSize: string; fontWeight: number };
      default: { fontSize: string; fontWeight: number };
      small: { fontSize: string; fontWeight: number };
      minimum: { fontSize: string; fontWeight: number };
      tiny: { fontSize: string; fontWeight: number };
      link: { fontSize: string; fontWeight: number };
      agGridInput: { fontSize: string; fontFamily: string; fontWeight: number };
    };
    spacing: {
      (...args: number[]): string;
      unit: number;
    };
  }
}
